<rt-dialog-header
  [heading]="'app.offer_assignments.offer_team_members_dialog.heading' | translate"
  [subheading]="subheading"
>
</rt-dialog-header>

<rt-dialog-body layout-vertical>
  <rt-box variant="vertical background no-padding">
    <div class="search">
      <rt-form-field>
        <rt-input
          [placeholder]="'app.common.placeholders.search.team_members.other' | translate"
          [autoFocus]="true"
          [formControl]="searchControl"
          [value]="searchQuery"
          (valueChange)="onSearchQueryChange($event)"
          variant="rounded"
        >
          <rt-icon prefix name="search"></rt-icon>
          @if (isSearching()) {
            <rt-icon-button
              suffix
              (click)="cancelSearch()"
              variant="plain"
              shape="square"
              iconName="close"
              size="small"
            >
            </rt-icon-button>
          }
        </rt-input>
      </rt-form-field>
    </div>

    <rt-divider variant="line"></rt-divider>

    <div class="results">
      <div
        virtualScroller
        [bufferAmount]="5"
        [scrollAnimationTime]="0"
        [scrollThrottlingTime]="10"
        [executeRefreshOutsideAngularZone]="true"
        (vsUpdate)="onVirtualScrollUpdate($event)"
        [items]="virtualScrollItems"
        [style.height.px]="400"
      >
        <ng-template
          [ngTemplateOutletContext]="{ $implicit: virtualScrollUsers }"
          [ngTemplateOutlet]="membersListTpl"
        ></ng-template>
        @if (!!filteredDisallowedUsers.length) {
          @if (filteredUsers.length) {
            <rt-divider></rt-divider>
          }
          <div class="header" padding-lr padding-tb-small>
            <rt-heading variant="subheading">
              {{
                'app.offer_assignments.offer_team_members_dialog.cant_share_with'
                  | translate
                  | uppercase
              }}
            </rt-heading>
          </div>
        }
        <ng-template
          [ngTemplateOutlet]="disabledListTemplate"
          [ngTemplateOutletContext]="{ $implicit: virtualScrollDisabledUsers }"
        ></ng-template>
        @if (!filteredUsers.length && !filteredDisallowedUsers.length) {
          <rt-empty-state
            [icon]="'search'"
            [title]="'app.common.no_results_found' | translate"
            variant="small"
          >
            <rt-button (click)="cancelSearch()" variant="tertiary">
              {{ 'app.assignments.editor.clear_search' | translate }}
            </rt-button>
          </rt-empty-state>
        }
      </div>
    </div>
  </rt-box>

  @if (allowInvite && !invitationFormOpened && canManageRecruiters) {
    <rt-button (click)="toggleInvitationForm()" variant="tertiary" margin-top>
      <rt-icon name="add"></rt-icon>
      <span>{{ 'app.offer_assignments.offer_team_members_dialog.invite_button' | translate }}</span>
    </rt-button>
  }

  @if (invitationFormOpened) {
    <rt-box variant="vertical padding-small" margin-top>
      <div class="invitation-box-header" layout-horizontal layout-align-items-center>
        <rt-heading variant="heading large" layout-flex-1-0>
          {{ 'app.offer_assignments.offer_team_members_dialog.invite_form_title' | translate }}
        </rt-heading>
        <rt-icon [name]="'close'" (click)="toggleInvitationForm()"> </rt-icon>
      </div>
      <rt-invitation-form
        [roles]="roles"
        [currentAdminEmails]="currentAdminEmails"
        [invitedEmails]="invitedEmails"
        (valueChanges)="onValueChanges($event)"
      >
      </rt-invitation-form>
    </rt-box>
  }
</rt-dialog-body>

<rt-dialog-footer variant="background">
  <rt-button (click)="cancel()" variant="tertiary" [disabled]="loading" margin-right-small-x>
    {{ 'app.common.cancel' | translate }}
  </rt-button>
  <rt-button [loading]="loading" [disabled]="isFormValid()" (click)="submit()" variant="primary">
    {{
      invitations.length
        ? ('app.offer_assignments.offer_team_members_dialog.save_and_invite'
          | translate: { count: invitations.length })
        : ('app.common.save' | translate)
    }}
  </rt-button>
</rt-dialog-footer>

<ng-template #membersListTpl let-visibleUsers>
  @for (admin of visibleUsers; track trackById($index, admin)) {
    <div class="result">
      <rt-group
        [class.checked]="adminCheckbox.checked"
        (click)="toggleAssignment(admin)"
        variant="horizontal"
      >
        <div [rtTooltip]="disabledCheckboxTooltipText" [rtTooltipEnabled]="!!admin?.isDisabled">
          <rt-checkbox
            #adminCheckbox
            [checked]="assignmentsControl.value.includes(admin.id)"
            [disabled]="!!admin?.isDisabled"
            (valueChange)="toggleAssignment(admin)"
            padding-left-small-x
          >
          </rt-checkbox>
        </div>

        <rt-avatar
          [type]="admin.hasAvatar ? 'image' : 'initials'"
          [initials]="admin.initials"
          [size]="'base'"
          [shape]="'oval'"
          [imageSrc]="admin.photoThumbUrl"
          context="admin"
        ></rt-avatar>
        <div margin-left-small layout-vertical>
          <rt-heading>
            <span [rtTooltip]="admin.roleName" rtTooltipPosition="right" padding-right-small>
              {{ admin.firstName }} {{ admin.lastName }}
            </span>
            @if (admin.isApprover) {
              <rt-badge
                color="secondary"
                [text]="'app.common.approver' | translate"
                [rtTooltip]="badgesTooltipText"
                margin-right-small-x
              ></rt-badge>
            }
            @if (admin.isRecruiter) {
              <rt-badge
                color="secondary"
                [text]="'app.common.recruiter' | translate"
                [rtTooltip]="badgesTooltipText"
                margin-right-small-x
              ></rt-badge>
            }
            @if (admin.isHiringManager) {
              <rt-badge
                color="secondary"
                [text]="'app.common.hiring_manager' | translate"
                [rtTooltip]="badgesTooltipText"
                margin-right-small-x
              ></rt-badge>
            }
          </rt-heading>
          <rt-truncate>
            {{ admin.email }}
          </rt-truncate>
        </div>
      </rt-group>
      <rt-divider></rt-divider>
    </div>
  }

  @for (
    roleWithAdmins of rolesWithAdmins;
    track trackById($index, roleWithAdmins);
    let last = $last
  ) {
    <rt-team-members-item
      #teamMembersItem
      [roleWithAdmins]="roleWithAdmins"
      [hasNoBorderBottom]="last"
      [removable]="false"
      [hasPadding]="true"
      [hasManageSettingsButton]="canManageRecruiters"
      [hasCheckboxOffset]="true"
      [collapsable]="true"
      [roleDescription]="groupItemDescription(roleWithAdmins.count!)"
      (goToManageRole)="goToManageRole($event)"
      (itemExpanded)="scrollToTeamMembersItem(teamMembersItem)"
    >
    </rt-team-members-item>
  }
</ng-template>

<ng-template #disabledListTemplate let-visibleUsers>
  @for (admin of visibleUsers; track trackById($index, admin)) {
    <div (click)="$event.stopImmediatePropagation()" class="result result__disabled">
      <rt-group
        [rtTooltipMaxWidth]="200"
        [rtTooltip]="translations.offer.disabledUserTooltip"
        rtTooltipPosition="left"
        variant="horizontal"
      >
        <rt-checkbox [disabled]="true" padding-left-small-x> </rt-checkbox>

        <rt-avatar
          [type]="admin.hasAvatar ? 'image' : 'initials'"
          [initials]="admin.initials"
          [size]="'base'"
          [shape]="'oval'"
          [imageSrc]="admin.photoThumbUrl"
          context="admin"
        ></rt-avatar>
        <div class="admin-info" margin-left-small layout-vertical>
          <div>
            <span class="heading" padding-right-small>
              {{ admin.firstName }} {{ admin.lastName }}
            </span>
          </div>
          <rt-truncate>
            {{ admin.email }}
          </rt-truncate>
        </div>
      </rt-group>
      <rt-divider></rt-divider>
    </div>
  }
</ng-template>
